import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Truck, Calendar, User, MapPin } from 'lucide-react';
import TruckDetailsForm from './VehicleAssignmentsForm';
import apiClient from '../services/api';
import '../styles/ConsignmentList.css';

const VehicleAssignments = () => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();

  useEffect(() => {
    loadAssignments();
  }, []);

  const loadAssignments = async () => {
    setLoading(true);
    setError('');
    
    try {
      const companyId = session?.companyid;
      const response = await apiClient.get('/VehicleAssignments.php', {
        params: { companyid: companyId }
      });
      
      if (response.data.success) {
        const assignments = response.data.data || [];
        setAssignments(assignments);
      } else {
        throw new Error(response.data.message || 'Failed to load');
      }
    } catch (err) {
      console.error('Error loading assignments:', err);
      
      let errorMessage = 'Failed to load assignments';
      
      if (err.response) {
        if (err.response.status === 401) {
          errorMessage = 'Session expired. Please login again.';
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
        } else {
          errorMessage = err.response.data?.message || errorMessage;
        }
      } else if (err.request) {
        errorMessage = 'Server not responding. Please check if backend is running.';
      } else {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setEditData(null);
    setShowForm(true);
  };

  const handleEdit = (assignment) => {
    setEditData(assignment);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) {
      return;
    }

    try {
      const response = await apiClient.delete(`/VehicleAssignments.php?id=${id}`);
      
      if (response.data.success) {
        alert('Assignment deleted successfully!');
        loadAssignments();
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (err) {
      console.error('Error deleting:', err);
      alert('Error deleting assignment: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSave = async (formData) => {
    try {
      const isEdit = formData.AssignmentID;
      
      let response;
      if (isEdit) {
        response = await apiClient.put(`/VehicleAssignments.php?id=${formData.AssignmentID}`, formData);
      } else {
        response = await apiClient.post('/VehicleAssignments.php', formData);
      }
      
      if (response.data.success) {
        alert(isEdit 
          ? 'Assignment updated successfully!' 
          : 'Assignment created successfully!'
        );
        setShowForm(false);
        setEditData(null);
        loadAssignments();
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      console.error('Error saving:', err);
      throw new Error(err.response?.data?.message || err.message);
    }
  };

  const handleClose = () => {
    setShowForm(false);
    setEditData(null);
  };

  if (!session?.companyid) {
    return (
      <div className="consignment-page">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  const filteredAssignments = assignments.filter(a =>
    a.VehicleNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.DriverName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.Origin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.Destination?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'Assigned': return 'blue';
      case 'In Progress': return 'yellow';
      case 'Completed': return 'green';
      case 'Cancelled': return 'red';
      default: return 'default';
    }
  };

  return (
    <div className="consignment-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <div className="header-icon" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
            <Truck size={28} />
          </div>
          <div>
            <h1>Truck Details & Assignments</h1>
            <p className="header-subtitle">Manage daily vehicle and driver assignments</p>
          </div>
        </div>
        <button className="btn-primary" onClick={handleNew}>
          <Plus size={20} />
          New Assignment
        </button>
      </div>

      {/* Toolbar */}
      <div className="page-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by vehicle, driver, route..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="toolbar-info">
          <span className="count-badge" style={{ background: '#f59e0b' }}>{filteredAssignments.length}</span>
          {filteredAssignments.length === 1 ? 'assignment' : 'assignments'}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading assignments...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
          <button className="btn-primary" onClick={loadAssignments}>
            Retry
          </button>
        </div>
      ) : filteredAssignments.length === 0 ? (
        <div className="empty-state">
          <Truck size={48} style={{ opacity: 0.3 }} />
          <h3>No assignments found</h3>
          <p>
            {searchTerm 
              ? 'Try adjusting your search terms' 
              : 'Get started by creating your first assignment'}
          </p>
          {!searchTerm && (
            <button className="btn-primary" onClick={handleNew}>
              <Plus size={20} />
              New Assignment
            </button>
          )}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Vehicle</th>
                <th>Driver</th>
                <th>Route</th>
                <th>Departure</th>
                <th>Arrival</th>
                <th>Distance (KM)</th>
                <th>Status</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAssignments.map(assignment => (
                <tr key={assignment.AssignmentID}>
                  <td>
                    <div className="date-cell">
                      <Calendar size={14} />
                      {new Date(assignment.AssignmentDate).toLocaleDateString()}
                    </div>
                  </td>
                  <td>
                    <div className="customer-cell">
                      <Truck size={14} />
                      <div>
                        <div className="customer-name">{assignment.VehicleNo || '-'}</div>
                        <div className="customer-sub">{assignment.VehicleType || ''}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="customer-cell">
                      <User size={14} />
                      <div>
                        <div className="customer-name">{assignment.DriverName || '-'}</div>
                        <div className="customer-sub">{assignment.DriverPhone || ''}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="route-cell">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <MapPin size={12} />
                        {assignment.Origin || 'N/A'}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <MapPin size={12} />
                        {assignment.Destination || 'N/A'}
                      </div>
                      {assignment.Route && (
                        <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '2px' }}>
                          Via: {assignment.Route}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    {assignment.DepartureTime || '-'}
                  </td>
                  <td>
                    {assignment.ArrivalTime || '-'}
                  </td>
                  <td>
                    {assignment.Odometer_Start && assignment.Odometer_End ? (
                      <div style={{ fontFamily: 'monospace', fontWeight: '600' }}>
                        {(parseFloat(assignment.Odometer_End) - parseFloat(assignment.Odometer_Start)).toFixed(2)}
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    <span className={`status-badge status-${getStatusColor(assignment.Status)}`}>
                      {assignment.Status || 'Assigned'}
                    </span>
                  </td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(assignment)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(assignment.AssignmentID)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <TruckDetailsForm
          onClose={handleClose}
          onSave={handleSave}
          editData={editData}
        />
      )}
    </div>
  );
};

export default VehicleAssignments;